import {useLoadMore} from '@/common-pages/hooks/load-more.hooks';
import DetailNavTitle from '@/components/business/detail-nav-title';
import {cn, copyText, goBack} from '@/utils';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Image, InfiniteScroll} from 'antd-mobile';
import Spin from '@/components/basic/spin';
import theme from '@/style';
import dayjs from 'dayjs';
type RecordStatusType = 'PENDING' | 'UNISSUED' | 'ISSUED';
import {ReactComponent as CopyIcon} from '@components/assets/icons/proxy/home/copy.svg';
import NoData from '@/components/basic/error-pages/no-data';
import {getUserSpinRecord} from './service';
import {getDisplayPrizeName} from './modal/prize';

const RecordStatusRow = ({status}: {status: RecordStatusType}) => {
  const {t} = useTranslation();
  const colors = useMemo(() => {
    const map = {
      PENDING: {
        icon: null,
        text: t('label.pending'),
        bgImage:
          'linear-gradient(90deg, rgba(23, 118, 255, 0.08) 0.04%, rgba(23, 118, 255, 0.00) 99.94%)',
        color: '#1776FF',
      },
      ISSUED: {
        icon: require('@/components/assets/icons/records-list-item/complete.webp'),
        text: t('label.issued'),
        bgImage:
          'linear-gradient(90deg, rgba(3, 201, 58, 0.08) 0.04%, rgba(3, 201, 58, 0.00) 99.94%)',
        color: '#009919',
      },
      UNISSUED: {
        icon: null,
        text: t('label.unissued'),
        bgImage:
          'linear-gradient(90deg, rgba(201, 3, 3, 0.08) 0.04%, rgba(201, 3, 3, 0.00) 99.94%)',
        color: '#C90303',
      },
    };
    return map[status];
  }, [status, t]);
  return (
    <div
      className="flex flex-row items-center justify-between px-3 h-7 rounded-l-full"
      style={{
        background: colors.bgImage,
      }}>
      <div className="flex flex-row gap-1 items-center">
        {!!colors.icon && <Image src={colors.icon} className="size-3" />}
        <span className="text-xs uppercase" style={{color: colors.color}}>
          {colors.text}
        </span>
      </div>
    </div>
  );
};

interface RecordItemProps {
  icon: string;
  title: string;
  money: string;
  status: RecordStatusType;
  defaultShowCount?: number;
  otherOptions: {
    label: string;
    value: string;
    canCopy?: boolean;
  }[];
}

const RecordItem = ({
  status,
  icon,
  title,
  money,
  otherOptions,
  defaultShowCount = 3,
}: RecordItemProps) => {
  const [showMore, setShowMore] = useState(false);
  return (
    <div className="flex flex-col bg-white rounded-lg py-2 px-3">
      <RecordStatusRow status={status} />
      <div
        className="flex flex-row items-center justify-between h-10"
        onClick={() => setShowMore(!showMore)}>
        <div className="flex flex-row items-center gap-3">
          <Image src={icon} className="size-6" />
          <span className="text-t2 text-sm font-bold capitalize">{title}</span>
        </div>
        <div className="flex flex-row items-center gap-2">
          <span
            className={cn(
              'text-base font-bold capitalize font-din',
              status === 'PENDING' || status === 'ISSUED'
                ? 'text-t1'
                : 'text-t4',
            )}>
            {money}
          </span>
          {otherOptions.length > defaultShowCount ? (
            <div
              className={cn(
                'size-3.5 bg-full bg-no-repeat',
                showMore ? 'rotate-180' : 'rotate-0',
              )}
              style={{
                backgroundImage: `url(${require('@/components/assets/icons/records-list-item/arrow-down.webp')})`,
              }}
            />
          ) : null}
        </div>
      </div>
      {(showMore ? otherOptions : otherOptions.slice(0, defaultShowCount)).map(
        item => (
          <div
            key={item.label}
            className="flex flex-row items-center justify-between h-10 text-xs">
            <span className="text-t3">{item.label}</span>
            <div className="text-t2 flex flex-row items-center gap-2">
              {item.canCopy ? (
                <>
                  <span>{item.value}</span>
                  <Button
                    className="bg-transparent p-0"
                    onClick={() => copyText(item.value)}>
                    <CopyIcon className="size-3.5" />
                  </Button>
                </>
              ) : (
                item.value
              )}
            </div>
          </div>
        ),
      )}
    </div>
  );
};

const WinningRecord = () => {
  const {t} = useTranslation();
  const pageSize = 10;
  const {list, loading, hasMore, loadMore, reload} = useLoadMore(
    async pageNo => {
      // const turnConfigJson = localStorage.getItem('turnableConfig');
      // if (!turnConfigJson) {
      //   return {
      //     list: [],
      //     total: 0,
      //   };
      // }
      try {
        // const turnConfig = JSON.parse(turnConfigJson);
        const res = await getUserSpinRecord({
          // turntableId: turnConfig.id,
          pageNo,
          pageSize,
        });
        return {
          list: res,
          total:
            res.length > 0
              ? res.length < pageSize
                ? 0
                : pageSize * pageNo + 1
              : 0,
        };
      } catch (e) {
        console.log('json parse error turnableConfig');
        return {
          list: [],
          total: 0,
        };
      }
    },
    pageSize,
  );
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="flex flex-col w-full h-[100vh] bg-bg">
      <DetailNavTitle
        title={t('spinActual.label.winRecord')}
        onBack={() => goBack()}
        hideAmount
        hideServer
      />
      <Spin loading={loading} style={[theme.flex.flex1]}>
        <div className="flex flex-col p-3 gap-3 flex-1 overflow-y-auto">
          {list.map(item => (
            <RecordItem
              icon={require('@assets/icons/spin-actual/won.webp')}
              title={t('label.won')}
              money={getDisplayPrizeName(item.awardName)}
              status={
                item.status === 0
                  ? 'PENDING'
                  : item.status === 1
                  ? 'UNISSUED'
                  : 'ISSUED'
              }
              otherOptions={[
                {
                  label: t('spinActual.label.winTime'),
                  value: item.createTime
                    ? dayjs(item.createTime).format('DD/MM/YYYY hh:mm A')
                    : '-',
                },
                {
                  label: t('spinActual.label.orderNo'),
                  value: item.orderNo || '-',
                  canCopy: true,
                },
              ]}
            />
          ))}
          {list.length > 0 ? (
            <InfiniteScroll hasMore={hasMore} loadMore={loadMore}>
              {hasMore ? t('label.loading') : t('label.noMore')}
            </InfiniteScroll>
          ) : (
            <NoData />
          )}
        </div>
      </Spin>
    </div>
  );
};

export default WinningRecord;
